import { FC, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';

interface InvitationDetails {
  id: string;
  orgId: string;
  orgName: string;
  email: string;
  name?: string;
  role?: string;
  status: string;
  invitedBy: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_BASE = process.env.REACT_APP_EOS_API_2 ?? '';

const brandColors = {
  primary: '#191C43',
  secondary: '#848AD1',
  accent: '#d62223',
  bg: '#f7fbfb',
  white: '#ffffff',
  text: '#242424',
  textLight: '#5c5c5c',
  textMuted: '#919191',
  border: '#e5e5e5',
  gradient: 'linear-gradient(135deg, #2c3277 0%, #191c43 100%)',
};

const Invitation: FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (!invitationId) return;

    const fetchInvitation = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/invitations/orgs/${invitationId}`);
        const data: ApiResponse<InvitationDetails> = await response.json();
        if (data.success && data.data) {
          setInvitation(data.data);
          if (data.data.status === 'accepted') {
            setAccepted(true);
          }
        } else {
          setError(data.error || 'Invitation not found');
        }
      } catch {
        setError('Failed to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    void fetchInvitation();
  }, [invitationId]);

  useEffect(() => {
    if (action === 'accept' && invitation && !accepted && !processing) {
      void handleAccept();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, invitation]);

  useEffect(() => {
    if (action === 'decline' && invitation && !declined) {
      setDeclined(true);
    }
  }, [action, invitation, declined]);

  const handleAccept = async (): Promise<void> => {
    setProcessing(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/invitations/orgs/${invitationId ?? ''}/accept`,
        {
          method: 'POST',
        },
      );
      const data: ApiResponse<{ isNewUser: boolean }> = await response.json();
      if (data.success) {
        setAccepted(true);
        setIsNewUser(data.data?.isNewUser ?? false);
      } else {
        setError(data.error || 'Failed to accept invitation');
      }
    } catch {
      setError('Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = (): void => {
    setDeclined(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: brandColors.bg }}
      >
        <CircularProgress sx={{ color: brandColors.primary }} />
      </Box>
    );
  }

  if (error && !invitation) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: brandColors.bg }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            m: 2,
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(25, 28, 67, 0.08)',
          }}
        >
          <Box sx={{ background: brandColors.gradient, py: 3, px: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: brandColors.white, fontWeight: 600 }}>
              Edit On The Spot
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5" sx={{ color: brandColors.primary, mb: 1 }}>
              Invitation Not Found
            </Typography>
            <Typography sx={{ color: brandColors.textLight }}>{error}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: brandColors.bg }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            m: 2,
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(25, 28, 67, 0.08)',
          }}
        >
          <Box sx={{ background: brandColors.gradient, py: 3, px: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: brandColors.white, fontWeight: 600 }}>
              Edit On The Spot
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: 28,
              }}
            >
              &#10003;
            </Box>
            <Typography variant="h5" sx={{ color: brandColors.primary, mb: 1 }}>
              Invitation Accepted!
            </Typography>
            <Typography sx={{ color: brandColors.textLight, mb: 3 }}>
              You have joined <strong>{invitation?.orgName}</strong>
              {invitation?.role ? ` as a ` : ''}
              {invitation?.role && (
                <strong style={{ color: brandColors.secondary }}>{invitation.role}</strong>
              )}
              .
            </Typography>
            {isNewUser ? (
              <Typography sx={{ color: brandColors.textLight, fontSize: 14 }}>
                Your login credentials have been sent to{' '}
                <strong style={{ color: brandColors.primary }}>{invitation?.email}</strong>. Please
                check your email to log in.
              </Typography>
            ) : (
              <Button
                variant="contained"
                href="/projects"
                sx={{
                  background: brandColors.primary,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': { background: '#2c3277' },
                }}
              >
                Go to Login
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (declined) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: brandColors.bg }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            m: 2,
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(25, 28, 67, 0.08)',
          }}
        >
          <Box sx={{ background: brandColors.gradient, py: 3, px: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: brandColors.white, fontWeight: 600 }}>
              Edit On The Spot
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5" sx={{ color: brandColors.primary, mb: 1 }}>
              Invitation Declined
            </Typography>
            <Typography sx={{ color: brandColors.textLight }}>
              You have declined the invitation to join <strong>{invitation?.orgName}</strong>.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (processing) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: brandColors.bg }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            m: 2,
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(25, 28, 67, 0.08)',
          }}
        >
          <Box sx={{ background: brandColors.gradient, py: 3, px: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: brandColors.white, fontWeight: 600 }}>
              Edit On The Spot
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CircularProgress sx={{ color: brandColors.primary, mb: 2 }} />
            <Typography sx={{ color: brandColors.textLight }}>Processing invitation...</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ background: brandColors.bg }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          m: 2,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(25, 28, 67, 0.08)',
        }}
      >
        <Box sx={{ background: brandColors.gradient, py: 3, px: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: brandColors.white, fontWeight: 600 }}>
            Edit On The Spot
          </Typography>
        </Box>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ color: brandColors.primary, mb: 2 }}>
            You&#39;re Invited!
          </Typography>
          <Typography sx={{ color: brandColors.textLight, mb: 1 }}>
            <strong style={{ color: brandColors.text }}>{invitation?.invitedBy}</strong> has invited
            you to join:
          </Typography>
          <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 1 }}>
            {invitation?.orgName}
          </Typography>
          {invitation?.role && (
            <Typography sx={{ color: brandColors.textLight, mb: 3 }}>
              Role: <strong style={{ color: brandColors.secondary }}>{invitation.role}</strong>
            </Typography>
          )}

          {error && (
            <Typography sx={{ color: brandColors.accent, mb: 2, fontSize: 14 }}>{error}</Typography>
          )}

          <Box display="flex" gap={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => void handleAccept()}
              sx={{
                background: brandColors.primary,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': { background: '#2c3277' },
              }}
            >
              Accept Invitation
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleDecline}
              sx={{
                color: brandColors.primary,
                borderColor: brandColors.primary,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#2c3277',
                  background: 'rgba(25, 28, 67, 0.04)',
                },
              }}
            >
              Decline
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Invitation;
